import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DEFAULT_CURRENCY_CODE, ErrorHandler, NgModule, isDevMode } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouterModule, TitleStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthHttpInterceptor, AuthModule, HttpMethod } from '@auth0/auth0-angular';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsModule } from '@ngxs/store';
import { enUS } from 'date-fns/locale';
import { provideToastr } from 'ngx-toastr';

import { ActionsState } from '@troyai/actions/data-access';
import { ActivityState } from '@troyai/activity/data-access';
import { AdherenceState } from '@troyai/adherence/data-access';
import {
  ADHERENCE_CONFIG_TOKEN,
  AdherenceService,
  adherencePercentagesConfig,
} from '@troyai/adherence/services';
import { UserState, getAuth0RedirectUri } from '@troyai/auth/data-access';
import { RolesDirective, UserRolesService } from '@troyai/auth/feature';
import { CareManagementState } from '@troyai/care-management/data-access';
import { AssessmentsViewerState } from '@troyai/portal/assessments-viewer/data-access';
import { CarePlansState, CaseNotesState } from '@troyai/case-notes/data-access';
import { DashboardState } from '@troyai/dashboard/data-access';
import { PIIAnonymizerInterceptor } from '@troyai/demo-mode';
import { Environment } from '@troyai/envs';
import { GlobalErrorHandler, HttpErrorInterceptor } from '@troyai/error-handling';
import { FeatureFlagDirective } from '@troyai/feature-flags';
import { CareState } from '@troyai/hra/data-access';
import { MinimalLayoutComponent, SidebarLayoutComponent } from '@troyai/layout';
import { LeaderboardState } from '@troyai/leaderboard/data-access';
import { MedicationState } from '@troyai/medication/medication-data-access';
import { MainMenuComponent } from '@troyai/navigation';
import { NotificationsState } from '@troyai/notifications/data-access';
import { NotificationsCountBadgeComponent } from '@troyai/notifications/ui';
import { OnboardingState } from '@troyai/onboarding/data-access';
import { CircleOfCareState, ContactInfoState, PatientsState } from '@troyai/patients/data-access';
import { FullNamePipe } from '@troyai/patients/util';
import { PaymentsState } from '@troyai/payments/data-access';
import {
  PharmacyContextState,
  SelectedPharmacyInterceptor,
} from '@troyai/pharmacy-context/data-access';
import { BrandingComponent } from '@troyai/portal/branding';
import { ClinicalObservationsState } from '@troyai/portal/clinical-observations/data-access';
import { CustomPageTitleStrategy } from '@troyai/portal/common/navigation';
import { LocalDatePipe } from '@troyai/portal/common/pipes';
import { DiagnosesState } from '@troyai/portal/diagnoses/data-access';
import { DocumentsState } from '@troyai/portal/documents/data-access';
import { DynamicFormState } from '@troyai/portal/dynamic-forms/data-access';
import {
  ClinicalEventEvidenceState,
  HealthTimelineState,
  RecordChangesState,
} from '@troyai/portal/health-timeline/data-access';
import { InterventionEpisodesState } from '@troyai/portal/intervention-episodes/data-access';
import { DemoControlsComponent, provideDemoBackend } from '@troyai/portal/investor-demo/backend';
import { RootComponentsComponent } from '@troyai/portal/root-components';
import { ThemeService } from '@troyai/portal/theme';
import { AircallState } from '@troyai/portal/voice-calls/data-access';
import {
  PriorAuthorizationsState,
  SinglePatientPriorAuthorizationState,
} from '@troyai/prior-authorization/data-access';
import { PwaService } from '@troyai/pwa';
import { ResourcesState } from '@troyai/resources/data-access';
import {
  API_BASE_URL,
  HttpCachingInterceptor,
  RestApiClientService,
} from '@troyai/rest-api-client';
import { SelfHostedPostHog, TRACKER_PLUGIN, TrackService } from '@troyai/tracking';
import {
  IconsModule,
  LoaderComponent,
  MinimizedModalsTrayComponent,
  ToastComponent,
} from '@troyai/ui-kit';
import { UserProfileState } from '@troyai/user-profile/data-access';
import { TenantRefInterceptor, TenantService } from 'multi-tenancy';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { APP_ROUTES } from './app.routes';

let devImports = [NgxsReduxDevtoolsPluginModule.forRoot()];

if (environment.production) {
  devImports = [];
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    SidebarLayoutComponent,
    MinimalLayoutComponent,
    IconsModule,
    MainMenuComponent,
    LoaderComponent,
    NotificationsCountBadgeComponent,
    DemoControlsComponent,
    NgxsModule.forRoot(
      [
        UserState,
        UserProfileState,
        PatientsState,
        ActionsState,
        ResourcesState,
        CareState,
        PriorAuthorizationsState,
        SinglePatientPriorAuthorizationState,
        NotificationsState,
        DashboardState,
        AdherenceState,
        PaymentsState,
        PharmacyContextState,
        LeaderboardState,
        ActivityState,
        OnboardingState,
        CaseNotesState,
        CarePlansState,
        CareManagementState,
        MedicationState,
        DiagnosesState,
        DynamicFormState,
        ClinicalObservationsState,
        HealthTimelineState,
        RecordChangesState,
        ClinicalEventEvidenceState,
        InterventionEpisodesState,
        ContactInfoState,
        CircleOfCareState,
        DocumentsState,
        AircallState,
        AssessmentsViewerState,
      ],
      {
        selectorOptions: {
          suppressErrors: false,
          injectContainerState: false,
        },
        developmentMode: !environment.production,
      }
    ),
    RouterModule.forRoot(APP_ROUTES, {
      paramsInheritanceStrategy: 'always',
    }),
    AuthModule.forRoot({
      domain: environment.auth0ConnectionData.domain,
      clientId: environment.auth0ConnectionData.clientId,
      // Undefined in prod/staging → SDK keeps its in-memory / silent-auth defaults.
      // Enabled only in the local/dev environments to survive page refreshes there.
      useRefreshTokens: environment.auth0ConnectionData.useRefreshTokens,
      cacheLocation: environment.auth0ConnectionData.cacheLocation,
      authorizationParams: {
        audience: environment.auth0ConnectionData.audience,
        // offline_access is required for Auth0 to issue a refresh token.
        scope: environment.auth0ConnectionData.useRefreshTokens
          ? 'profile email offline_access'
          : 'profile email',
        redirect_uri: getAuth0RedirectUri(),
      },
      httpInterceptor: {
        allowedList: [
          {
            uri: `${environment.apiUrl}/*`,
            httpMethod: HttpMethod.Get,
          },
          {
            uri: `${environment.apiUrl}/*`,
            httpMethod: HttpMethod.Post,
          },
          {
            uri: `${environment.apiUrl}/*`,
            httpMethod: HttpMethod.Patch,
          },
          {
            uri: `${environment.apiUrl}/*`,
            httpMethod: HttpMethod.Delete,
          },
          {
            uri: `${environment.apiUrl}/*`,
            httpMethod: HttpMethod.Put,
          },
        ],
      },
    }),
    RolesDirective,
    FeatureFlagDirective,
    BrandingComponent,
    RootComponentsComponent,
    MinimizedModalsTrayComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 15 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:15000',
    }),
    ...devImports,
  ],
  providers: [
    // CDK 21 renders overlays in the browser's top layer (`popover="manual"`) by default.
    // Top-layer elements always paint above normal content, so ngx-toastr — which uses its
    // own container on `document.body`, not the CDK overlay — became invisible behind any
    // open modal. Opting out restores the CDK 20 z-index-based stacking.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    provideAnimations(),
    provideToastr({
      timeOut: 5000,
      toastComponent: ToastComponent,
      closeButton: true,
      tapToDismiss: false,
      onActivateTick: true,
    }),
    TenantService,
    UserRolesService,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Investor demo: first, so it is the outermost interceptor. Inert unless demo mode is on.
    ...provideDemoBackend(),
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpCachingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SelectedPharmacyInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PIIAnonymizerInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: TenantRefInterceptor, multi: true },
    {
      provide: Environment,
      useValue: environment,
    },
    {
      provide: RestApiClientService,
      useClass: RestApiClientService,
    },
    {
      provide: API_BASE_URL,
      useValue: environment.apiUrl,
    },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'USD' },
    {
      provide: TitleStrategy,
      useClass: CustomPageTitleStrategy,
    },
    AdherenceService,
    {
      provide: ADHERENCE_CONFIG_TOKEN,
      useValue: adherencePercentagesConfig,
    },
    FullNamePipe,
    TitleCasePipe,
    LocalDatePipe,
    TrackService,
    { provide: TRACKER_PLUGIN, useClass: SelfHostedPostHog, multi: true },
    PwaService,
    ThemeService,
    { provide: MAT_DATE_LOCALE, useValue: enUS },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
